// Helpers para el dashboard de admin.
// Todo síncrono y puro: recibe datos, devuelve datos derivados.

const DAY_MS = 24 * 60 * 60 * 1000;

// Devuelve fecha (YYYY-MM-DD) sin hora.
export function dayKey(date) {
  const d = new Date(date);
  return d.toISOString().split("T")[0];
}

// Inicio del día actual.
export function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// Devuelve { from, to } para los últimos N días (incluye hoy).
export function lastNDaysRange(days) {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * DAY_MS);
  from.setHours(0, 0, 0, 0);
  return { from, to };
}

// Cuenta items con timestamp en el rango.
export function countInRange(items, key, from, to) {
  return items.filter((it) => {
    const t = new Date(it[key]).getTime();
    return t >= from.getTime() && t <= to.getTime();
  }).length;
}

// Cuenta items DISTINCT por campo en el rango.
export function distinctInRange(items, key, dateKey, from, to) {
  const set = new Set();
  for (const it of items) {
    const t = new Date(it[dateKey]).getTime();
    if (t >= from.getTime() && t <= to.getTime() && it[key]) {
      set.add(it[key]);
    }
  }
  return set.size;
}

// Calcula tendencia: % cambio del actual vs anterior.
// Si anterior es 0 y actual > 0 -> 100. Si ambos 0 -> 0.
export function trendPct(current, previous) {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

// Determina el bloque actual de un alumno: el primer bloque (por block_order)
// que NO está completamente terminado. Si todos lo están -> "Finalizado".
export function getCurrentBlock(studentId, templates, progress) {
  const completedSet = new Set(
    progress
      .filter((p) => p.student_id === studentId && p.completed)
      .map((p) => p.template_id)
  );

  const blocks = new Map();
  for (const t of templates) {
    const entry = blocks.get(t.block_order) || {
      order: t.block_order,
      name: t.block_name,
      total: 0,
      done: 0,
    };
    entry.total += 1;
    if (completedSet.has(t.id)) entry.done += 1;
    blocks.set(t.block_order, entry);
  }

  const ordered = [...blocks.values()].sort((a, b) => a.order - b.order);
  for (const b of ordered) {
    if (b.done < b.total) return { order: b.order, name: b.name };
  }
  return { order: 999, name: "Finalizado" };
}

// Distribución de alumnos por bloque actual. Devuelve array ordenado.
export function distributionByBlock(students, templates, progress) {
  const blockOrder = [];
  const seen = new Set();
  for (const t of [...templates].sort((a, b) => a.block_order - b.block_order)) {
    if (!seen.has(t.block_order)) {
      seen.add(t.block_order);
      blockOrder.push({ order: t.block_order, name: t.block_name, count: 0 });
    }
  }
  blockOrder.push({ order: 999, name: "Finalizado", count: 0 });

  for (const s of students) {
    const b = getCurrentBlock(s.id, templates, progress);
    const slot = blockOrder.find((x) => x.order === b.order);
    if (slot) slot.count += 1;
  }
  return blockOrder;
}

// Distribución genérica por campo de students (programa, stage, etc.)
export function distributionByField(students, field, fallback = "Sin definir") {
  const map = new Map();
  for (const s of students) {
    const key = (s[field] || fallback).trim() || fallback;
    map.set(key, (map.get(key) || 0) + 1);
  }
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

// Logins agrupados por día para los últimos N días.
// Devuelve array de { date: 'DD/MM', count }.
export function loginsByDay(logins, days = 30) {
  const counts = new Map();
  const today = startOfToday();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getTime() - i * DAY_MS);
    counts.set(dayKey(d), { date: d, count: 0 });
  }
  for (const l of logins) {
    const k = dayKey(l.logged_in_at);
    if (counts.has(k)) counts.get(k).count += 1;
  }
  return [...counts.values()].map((it) => ({
    label: it.date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
    }),
    count: it.count,
  }));
}

// Cuenta alumnos que han completado TODAS las plantillas activas.
export function countFinished(students, templates, progress) {
  if (templates.length === 0) return 0;
  const activeIds = new Set(templates.map((t) => t.id));
  return students.filter((s) => {
    const done = progress.filter(
      (p) => p.student_id === s.id && p.completed && activeIds.has(p.template_id)
    ).length;
    return done >= templates.length;
  }).length;
}

// Días desde la última fecha (o null).
export function daysSince(iso) {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  return Math.floor(diff / DAY_MS);
}
